import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { Container, Row } from "reactstrap";
import Accordion from "react-bootstrap/Accordion";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import axiosInstance from "../../../Utils/axiosInstance";
import { CompactSelect } from "../../../Components/SharedComponents/SharedComponents";

/* ------------------ API HOOKS ------------------ */
const useGetContract_Name = (unit: string) => {
  return useQuery<any>({
    queryKey: ["Contract_Name", unit],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/paper-app/hedging/`, {
        params: { trade_unit: unit },
      });
      return data; // ✅ returns {dict1:[], dict2:[]}
    },
    enabled: !!unit,
    refetchOnWindowFocus: false,
  });
};

const useGetCustomerContract_Name = (unit: string, customer: string) => {
  return useQuery<any>({
    queryKey: ["customerContract_Name", unit, customer],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/paper-app/hedging/`, {
        params: { trade_unit: unit, cust_book: customer },
      });
      return data; // ✅ returns {dict1:[], dict2:[]}
    },
    enabled: !!unit && !!customer,
    refetchOnWindowFocus: false,
  });
};

const useGetCustomers = () => {
  return useQuery<any[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/paper-app/counterparty-companies`
      );
      return data.results || [];
    },
    refetchOnWindowFocus: false,
  });
};

/* ------------------ TABLE COMPONENT ------------------ */
const DataTable: React.FC<{
  data: any[];
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
}> = ({ data, isError, isFetching, isLoading }) => {
  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    if (!data?.length) return [];

    const keys = Object.keys(data[0]);
    const sortedKeys = [
      keys.includes("Contract_Name") ? "Contract_Name" : "Contracts",
      "HLD",
      ...keys.filter(
        (k) =>
          k !== "Contract_Name" &&
          k !== "Contracts" &&
          k !== "HLD" &&
          k !== "Monthly_Total"
      ),
      "Monthly_Total",
    ];

    return sortedKeys.map((key) => ({
      accessorKey: key,
      header:
        key === "Contract_Name" || key === "Contracts" ? "Contracts" : key,
      Cell: ({ cell }) => {
        const value = cell.getValue();

        if (value === "WKND") {
          return <Box sx={{fontSize: "0.7rem" }}>{value}</Box>;
        }

        if (typeof value === "number") {
          const color = value < 0 ? "#d32f2f" : value > 0 ? "#2e7d32" : "#555";
          return (
            <Box sx={{ color, fontWeight: 500, fontSize: "0.75rem" }}>
              {cell.getValue() as number}
            </Box>
          );
        }

        return <>{String(value)}</>;
      },
    }));
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnPinning: true,
    enableColumnOrdering: true,
    enableColumnDragging: true,
    enableColumnResizing: true,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableGrouping: true,
    enableExpanding: true,
    enableFilters: true,
    enableColumnFilters: true,
    enablePagination: true, 
    enableBottomToolbar: true,
    getRowId: (row) =>
      row.Contract_Name || row.Contracts || row.id || JSON.stringify(row),

    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    },

    muiTableContainerProps: {
      sx: {
        backgroundColor: "#fff",
        maxHeight: "600px",
        overflowY: "auto",
      },
      className: "table-container-wrapper__new",
    },

    muiTableFooterProps: {
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    },

    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    },

    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "0.75rem",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.7rem",
        padding: "4px 8px",
        borderBottom: "1px solid #f1f1f1",
        "&:hover": {
          backgroundColor: "#fafafa", // row hover effect
        },
      },
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "0",
      },
    },

    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,

    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    initialState: {
      density: "compact",
      showColumnFilters: false,
      grouping: [],
      expanded: true,
      pagination: { pageIndex: 0, pageSize: 50 },
    },
  });

  return (
    <div className="table-ad__wrapperCard">
      <MaterialReactTable table={table} />
    </div>
  );
};

/* ------------------ MAIN APP ------------------ */
const App: React.FC = () => {
  const [tab, setTab] = useState(0);

  // Hedging
  const [unit, setUnit] = useState<string>("lots");
  const {
    data: hedgingData = { dict1: [], dict2: [] },
    isError: hedgingError,
    isFetching: hedgingFetching,
    isLoading: hedgingLoading,
  } = useGetContract_Name(unit);

  // Customer
  const [custUnit, setCustUnit] = useState<string>("");
  const [customer, setCustomer] = useState<string>("");
  const {
    data: customerData = { dict1: [], dict2: [] },
    isError: customerError,
    isFetching: customerFetching,
    isLoading: customerLoading,
  } = useGetCustomerContract_Name(custUnit, customer);

  const { data: customerOptions = [] } = useGetCustomers();

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">

              {/* ✅ Accordion wrapper matching Broker/API Settings pattern */}
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Daily Exposure
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div className="flex formWrapper">
                        <Box
                          sx={{
                            width: "100%",
                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          {/* ✅ Tabs inside accordion body */}
                          <Box sx={{ mb: 0}}>
                            <Tabs
                              value={tab}
                              onChange={(_, v) => setTab(v)}
                              textColor="primary"
                              indicatorColor="primary"
                              sx={{
                                "& .MuiTab-root": {
                                  textTransform: "none",
                                  fontSize: "0.85rem",
                                  fontWeight: 500,
                                  borderRadius: "6px",
                                  minHeight: "36px",
                                  px: 2,
                                  mx: 0.5,
                                },
                                "& .Mui-selected": {
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                },
                              }}
                            >
                              <Tab label="Company Exposure" />
                              <Tab label="Customer Exposure" />
                            </Tabs>
                          </Box>
                        </Box>


                          {/* ✅ Company Exposure filters */}
                          {tab === 0 && (
                          <Box
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              width: "200px",
                              mt: 1,
                              gap: 0.5,
                            }}
                          >
                            <CompactSelect
                              label="Unit"
                              value={unit}
                              onChange={(value) => setUnit(value)}
                              options={[
                                { value: "kbbl", label: "kbbl" },
                                { value: "lots", label: "Lots" },
                              ]}
                              name="unit"
                              slotProps={{ inputLabel: { shrink: true } }}
                            />
                          </Box>
                          )}

                          {/* ✅ Customer Exposure filters */}
                          {tab === 1 && (
                          <Box
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              width: "450px",
                              mt: 1,  
                              gap: 0.5,
                            }}
                          >
                            <CompactSelect
                              label="Unit"
                              value={custUnit}
                              onChange={(value) => setCustUnit(value)}
                              options={[
                                { value: "kbbl", label: "kbbl" },
                                { value: "lots", label: "Lots" },
                              ]}
                              name="cust_unit"
                              slotProps={{ inputLabel: { shrink: true } }}
                            />

                            <CompactSelect
                              label="Customer"
                              value={customer}
                              onChange={(value) => setCustomer(value)}
                              options={customerOptions.map((c: any) => ({
                                value: c.customer_company_name,
                                label: c.customer_company_name,
                              }))}
                              name="customer"
                              slotProps={{ inputLabel: { shrink: true } }}
                            />
                          </Box>
                          )}
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* ✅ Table wrapper matching Broker/API Settings pattern */}
              <div className="table-ad__wrapper">

                {/* Company Exposure Tab */}
                {tab === 0 && (
                  <>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: "#2c3e50", mb: 0.5 , px: 0.5 ,textAlign: "left"}}
                    >
                      Paper Exposure
                    </Typography>
                    <DataTable
                      data={hedgingData.dict1}
                      isError={hedgingError}
                      isFetching={hedgingFetching}
                      isLoading={hedgingLoading}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: "#2c3e50", mb: 0.5, mt: 2, px: 0.5 ,textAlign: "left"}}
                    >
                      Paper Exposure Break By Book & Strategy
                    </Typography>
                    <DataTable
                      data={hedgingData.dict2}
                      isError={hedgingError}
                      isFetching={hedgingFetching}
                      isLoading={hedgingLoading}
                    />
                  </>
                )}

                {/* Customer Exposure Tab */}
                {tab === 1 && (
                  <>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: "#2c3e50", mb: 0.5, mt: 1, px: 0.5 ,textAlign: "left"}}
                    >
                      Paper Exposure
                    </Typography>
                    <DataTable
                      data={customerData.dict1}
                      isError={customerError}
                      isFetching={customerFetching}
                      isLoading={customerLoading}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ color: "#2c3e50", mb: 0.5, mt: 2, px: 0.5 ,textAlign: "left"}}
                    >
                      Paper Exposure Break By Book & Strategy
                    </Typography>
                    <DataTable
                      data={customerData.dict2}
                      isError={customerError}
                      isFetching={customerFetching}
                      isLoading={customerLoading}
                    />
                  </>
                )}

              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

/* ------------------ APP WRAPPER ------------------ */
const queryClient = new QueryClient();

export default function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}
